"use client";

import { useMemo, useState } from "react";

import { ORDER_TYPE, getProductOrderType } from "@/lib/orderType";

import {
  norm,
  productText,
  productBrand,
  variantPrice,
  productInStock,
  metadataValues,
  matchesCategory,
  matchesChip,
} from "@/lib/catalogFilterUtils";

export default function useDepartmentCatalogFilters({
  products,
  department,
  departmentSlug,
  initialQuery = "",
}) {
  const categoryNames = Object.keys(department.categories);

  /* =====================================================
     STATE
     ===================================================== */

  const [selectedCategory, setSelectedCategory] = useState(
    categoryNames[0] || "",
  );

  const [selectedChip, setSelectedChip] = useState("All");

  const [selectedBrands, setSelectedBrands] = useState([]);

  const [selectedOptions, setSelectedOptions] = useState({});

  const [stockOnly, setStockOnly] = useState(false);

  const [saleOnly, setSaleOnly] = useState(false);

  const [maxPrice, setMaxPrice] = useState("");

  const [sort, setSort] = useState("featured");

  const [query, setQuery] = useState(initialQuery);

  /* =====================================================
     DEPARTMENT PRODUCTS
     ===================================================== */

  const scopedProducts = useMemo(() => {
    const pageOrderType =
      departmentSlug === "take-out" ? ORDER_TYPE.TAKEOUT : ORDER_TYPE.GROCERY;

    const typedProducts = products.filter(
      (product) => getProductOrderType(product) === pageOrderType,
    );

    const departmentTarget = norm(department.label);

    const exactMatches = typedProducts.filter((product) => {
      const metadataDepartment = norm(product?.metadata?.department);

      const categories = (product?.categories || []).map((item) =>
        norm(item.name),
      );

      return (
        metadataDepartment === departmentTarget ||
        categories.some(
          (name) =>
            name === departmentTarget || name.includes(departmentTarget),
        )
      );
    });

    return exactMatches.length ? exactMatches : typedProducts;
  }, [products, department.label, departmentSlug]);

  /* =====================================================
     BRANDS
     ===================================================== */

  const brands = useMemo(() => {
    return [
      ...new Set(scopedProducts.map(productBrand).filter(Boolean)),
    ].sort();
  }, [scopedProducts]);

  const brandCounts = useMemo(() => {
    const counts = {};

    scopedProducts.forEach((product) => {
      const brand = productBrand(product);

      if (!brand) {
        return;
      }

      counts[brand] = (counts[brand] || 0) + 1;
    });

    return counts;
  }, [scopedProducts]);

  /* =====================================================
     DYNAMIC FILTERS
     ===================================================== */

  const dynamicValues = useMemo(() => {
    const result = {};

    for (const filter of department.filters) {
      if (filter.type !== "dynamic") {
        continue;
      }

      result[filter.key] = [
        ...new Set(
          scopedProducts.flatMap((product) =>
            metadataValues(product, filter.key),
          ),
        ),
      ].sort();
    }

    return result;
  }, [scopedProducts, department.filters]);

  /* =====================================================
     FILTER + SORT
     ===================================================== */

  const visibleProducts = useMemo(() => {
    let result = scopedProducts.filter(
      (product) =>
        matchesCategory(product, selectedCategory) &&
        matchesChip(product, selectedChip),
    );

    if (query.trim()) {
      const q = norm(query);

      result = result.filter((product) => productText(product).includes(q));
    }

    if (selectedBrands.length) {
      result = result.filter((product) =>
        selectedBrands.includes(productBrand(product)),
      );
    }

    if (stockOnly) {
      result = result.filter(productInStock);
    }

    if (saleOnly) {
      result = result.filter((product) =>
        Boolean(product?.metadata?.on_sale || product?.metadata?.sale),
      );
    }

    if (maxPrice) {
      result = result.filter(
        (product) => (variantPrice(product) ?? Infinity) <= Number(maxPrice),
      );
    }

    for (const [key, values] of Object.entries(selectedOptions)) {
      if (!values?.length) {
        continue;
      }

      result = result.filter((product) => {
        const productValues = metadataValues(product, key).map(norm);

        return values.some(
          (value) =>
            productValues.includes(norm(value)) ||
            productText(product).includes(norm(value)),
        );
      });
    }

    switch (sort) {
      case "name-asc":
        return [...result].sort((a, b) => a.title.localeCompare(b.title));

      case "name-desc":
        return [...result].sort((a, b) => b.title.localeCompare(a.title));

      case "price-low":
        return [...result].sort(
          (a, b) =>
            (variantPrice(a) ?? Infinity) - (variantPrice(b) ?? Infinity),
        );

      case "price-high":
        return [...result].sort(
          (a, b) =>
            (variantPrice(b) ?? -Infinity) - (variantPrice(a) ?? -Infinity),
        );

      default:
        return result;
    }
  }, [
    scopedProducts,
    selectedCategory,
    selectedChip,
    query,
    selectedBrands,
    stockOnly,
    saleOnly,
    maxPrice,
    selectedOptions,
    sort,
  ]);

  /* =====================================================
     ACTIONS
     ===================================================== */

  function selectCategory(category) {
    setSelectedCategory(category);

    setSelectedChip("All");
  }

  function toggleBrand(brand) {
    setSelectedBrands((current) =>
      current.includes(brand)
        ? current.filter((item) => item !== brand)
        : [...current, brand],
    );
  }

  function toggleOption(key, value) {
    setSelectedOptions((current) => {
      const values = new Set(current[key] || []);

      if (values.has(value)) {
        values.delete(value);
      } else {
        values.add(value);
      }

      return {
        ...current,

        [key]: [...values],
      };
    });
  }

  function clearFilters() {
    setSelectedCategory(categoryNames[0] || "");

    setSelectedChip("All");

    setSelectedBrands([]);

    setSelectedOptions({});

    setStockOnly(false);

    setSaleOnly(false);

    setMaxPrice("");

    setQuery("");
  }

  const chips = department.categories[selectedCategory] || ["All"];

  return {
    categoryNames,
    selectedCategory,
    selectedChip,

    selectedBrands,
    selectedOptions,

    stockOnly,
    saleOnly,
    maxPrice,

    sort,
    query,

    brands,
    brandCounts,
    dynamicValues,

    visibleProducts,
    chips,

    selectCategory,

    setSelectedChip,
    setStockOnly,
    setSaleOnly,
    setMaxPrice,
    setSort,
    setQuery,

    toggleBrand,
    toggleOption,
    clearFilters,
  };
}
